import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, Download, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ReceiptEditDialog } from './ReceiptEditDialog';
import { ReceiptDeleteDialog } from './ReceiptDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['Parking', 'Tolls', 'Fuel'];
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const pad = (n) => String(n).padStart(2, '0');
const isoLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthKey = (iso) => isoLocal(iso).slice(0, 7);
const monthLabel = (key) =>
  new Date(`${key}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const dateLabel = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const downloadCsv = (rows, driverName, month) => {
  const esc = (v) => {
    const s = String(v ?? '');
    return `"${(/^[=+\-@\t\r]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`;
  };
  const lines = [['Date', 'Receipt ID', 'Booking', 'Category', 'Amount (GBP)', 'Note', 'Receipt link']];
  rows.forEach((r) => lines.push([isoLocal(r.createdAt), r.id, r.bookingId || '', r.category, Number(r.amount || 0).toFixed(2), r.note, r.imageUrl || '']));
  lines.push(['', '', '', 'Total', rows.reduce((s, r) => s + Number(r.amount || 0), 0).toFixed(2), '', '']);
  const blob = new Blob([lines.map((l) => l.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `expenses-${(driverName || 'driver').replace(/\s+/g, '-').toLowerCase()}-${month}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const iconBtn = 'inline-flex p-1 rounded text-gray-500 hover:text-black hover:bg-gray-100 transition-colors';

const ReceiptRow = ({ r, onEdit, onDelete }) => (
  <div className="grid grid-cols-12 gap-3 items-center py-3 px-2 text-sm" data-testid={`receipt-row-${r.id}`}>
    <div className="col-span-2 text-gray-700">{dateLabel(r.createdAt)}</div>
    <div className="col-span-2">
      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-800">{r.category}</span>
    </div>
    <div className="col-span-2 text-xs text-gray-600 truncate">{r.bookingId || 'No booking'}</div>
    <div className="col-span-2 text-xs text-gray-600 truncate" title={r.note}>{r.note || '—'}</div>
    <div className="col-span-2 text-right font-semibold text-black" data-testid={`receipt-amount-${r.id}`}>{gbp(r.amount)}</div>
    <div className="col-span-2 flex justify-end items-center gap-1">
      {r.imageUrl && (
        <a href={r.imageUrl} target="_blank" rel="noreferrer" className={iconBtn} data-testid={`receipt-view-${r.id}`} title="View receipt">
          <ExternalLink className="h-4 w-4" />
        </a>
      )}
      <button type="button" onClick={() => onEdit(r)} className={iconBtn} title="Correct receipt" data-testid={`receipt-edit-${r.id}`}>
        <Pencil className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onDelete(r)} className={`${iconBtn} hover:text-red-600`} title="Delete receipt" data-testid={`receipt-delete-${r.id}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export const DriverReceipts = ({ driverId, driverName }) => {
  const [receipts, setReceipts] = useState([]);
  const [month, setMonth] = useState('all');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/drivers/${encodeURIComponent(driverId)}/receipts`)
      .then((res) => setReceipts(res.data.receipts || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load receipts'));
  }, [driverId]);

  const months = useMemo(() => [...new Set(receipts.map((r) => monthKey(r.createdAt)).filter(Boolean))], [receipts]);
  const rows = month === 'all' ? receipts : receipts.filter((r) => monthKey(r.createdAt) === month);
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const byCat = (c) => rows.filter((r) => r.category === c).reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <Card className="p-6" data-testid="driver-receipts-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-black flex items-center gap-2">
          <Receipt className="h-5 w-5 text-gray-500" /> Expense Receipts
        </h2>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="receipts-month-filter">
            <option value="all">All time</option>
            {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <Button size="sm" variant="outline" className="border-gray-300" disabled={!rows.length} onClick={() => downloadCsv(rows, driverName, month)} data-testid="receipts-download-csv">
            <Download className="h-4 w-4 mr-1.5" /> Download CSV
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[...CATEGORIES.map((c) => ({ label: c, value: byCat(c) })), { label: 'Total', value: total }].map((s) => (
          <div key={s.label} className={`rounded-md p-3 ${s.label === 'Total' ? 'bg-black text-white' : 'bg-gray-50'}`} data-testid={`receipts-total-${s.label.toLowerCase()}`}>
            <p className={`text-xs ${s.label === 'Total' ? 'text-gray-300' : 'text-gray-500'}`}>{s.label}</p>
            <p className="text-xl font-bold mt-0.5">{gbp(s.value)}</p>
          </div>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="receipts-empty">No receipts submitted{month !== 'all' ? ' this month' : ' yet'}.</p>
      ) : (
        <div className="divide-y divide-gray-100" data-testid="receipts-list">
          <div className="grid grid-cols-12 gap-3 px-2 pb-2 text-[11px] uppercase tracking-wider text-gray-500" data-testid="receipts-header">
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Booking</div>
            <div className="col-span-2">Note</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {rows.map((r) => <ReceiptRow key={r.id} r={r} onEdit={setEditing} onDelete={setDeleting} />)}
        </div>
      )}
      <ReceiptEditDialog
        receipt={editing}
        onClose={() => setEditing(null)}
        onSaved={(u) => { setReceipts((list) => list.map((x) => (x.id === u.id ? u : x))); setEditing(null); }}
      />
      <ReceiptDeleteDialog
        receipt={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(id) => { setReceipts((list) => list.filter((x) => x.id !== id)); setDeleting(null); }}
      />
    </Card>
  );
};
